import axios from 'axios'
import { config } from '../utils/config'
import {
  apiErrorHandler,
  shouldRetryOnUnauthorized,
  shouldRetryOnNetworkError,
  RETRY_FLAG,
  NETWORK_RETRY_FLAG,
} from './apiErrorHandler'
import { saveAs } from 'file-saver';

/**
 * Custom handler for status code
 * @callback StatusCodeHandler
 */

/**
 * @typedef {Object} StatusCodeHandlers
 * @prop {number} statusCode
 * @prop {StatusCodeHandler} handler
 */

/**
 * @typedef {Object} ApiOptions
 * @prop {Ojbect} axiosParams - see axios documentation for possible values
 * @prop {StatusCodeHandlers[]} statusCodeHandlers
 */

export function setAuthHeader(token) {
  axios.defaults.headers.common['Authorization'] = token ? 'Bearer ' + token : ''
}

// Set by App.js's SetAuthToken once it reaches the OIDC context - this module has no hook
// access of its own, so the retry below borrows renewTokens the same way setAuthHeader already
// borrows the access token.
let renewTokens = null
export function setRenewTokens(fn) {
  renewTokens = fn
}

const NETWORK_RETRY_DELAY_MS = 200

// A 401 anywhere mid-session gets one renew-and-retry. Under Service Worker mode the
// JS-visible access token is a fixed placeholder, so the worker attaching a real token to the
// retry is the only signal renewal worked. RETRY_FLAG bounds this to a single attempt.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Chromium does not retry a request that picked an HTTP/2 connection already draining after
    // GOAWAY, so it fails with no response. One replay of an idempotent request lands on a new one.
    if (shouldRetryOnNetworkError(error)) {
      error.config[NETWORK_RETRY_FLAG] = true
      await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS))
      return axios(error.config)
    }
    if (renewTokens && shouldRetryOnUnauthorized(error)) {
      error.config[RETRY_FLAG] = true
      let tokens
      try {
        tokens = await renewTokens()
      } catch {
        return Promise.reject(error)
      }
      // error.config's flattened header wins over axios.defaults on replay, so rewrite it with
      // the renewed token. Service Worker mode hands back no accessToken - leave the header
      // alone and let the worker attach it.
      if (tokens?.accessToken) {
        error.config.headers = { ...error.config.headers, Authorization: 'Bearer ' + tokens.accessToken }
        setAuthHeader(tokens.accessToken)
      }
      return axios(error.config)
    }
    return Promise.reject(error)
  }
)

export const BaseUrl = {
  Api: config.apiBaseUrl,
  Identity: config.identityBaseUrl,
  Centerpoint: config.centerpointBaseUrl
}

/**
 * @param {string} path 
 * @param {string} baseUrl 
 * @param {ApiOptions} options  
 */
export const get = async (path, baseUrl = BaseUrl.Api, options = null) => {
  // options = addSafeMessageExceptionHandlerToOptions(options);
  const axiosParams = options?.axiosParams || {}
    try {
    const result = await axios({
      method: "get",
      url: `${baseUrl}/${path}`,
      ...axiosParams
    })
    return result.data
  }
  catch (err) {
    console.log("get - err:", `${baseUrl}/${path}`, err);
    await apiErrorHandler(err, options?.statusCodeHandlers)
  }
}

/** 
 * @param {string} path 
 * @param {string} filename 
 * @param {string} baseUrl 
 * @param {ApiOptions} options 
 */
export const download = async (path, filename, baseUrl = BaseUrl.Api, options = null) => {
  const axiosParams = options?.axiosParams || {}
  try {
    const result = await axios({
      method: "get",
      responseType: 'blob', // important
      url: `${baseUrl}/${path}`,
      ...axiosParams
    })

    saveAs(result.data, filename);
  }
  catch (err) {
    await apiErrorHandler(err, options?.statusCodeHandlers)
  }
}

/** 
 * @param {string} path 
 * @param {string} filename 
 * @param {function} fnHandleData 
 * @param {string} baseUrl 
 * @param {ApiOptions} options 
 */
export const downloadEx = async (path, filename, fnHandleData = null, baseUrl = BaseUrl.Api, options = null) => {
  const axiosParams = options?.axiosParams || {}
  try {
    const result = await axios({
      method: "get",
      responseType: 'blob', // important
      url: `${baseUrl}/${path}`,
      ...axiosParams
    })

    if (!!fnHandleData)
      fnHandleData(result.data);
    saveAs(result.data, filename);

    return result.data
  }
  catch (err) {
    await apiErrorHandler(err, options?.statusCodeHandlers)
  }
}

/**
 * @param {string} path 
 * @param {object} data 
 * @param {ApiOptions} options 
 * @param {string} baseUrl 
 * @returns 
 */
export const put = async (path, data, options = null, baseUrl = BaseUrl.Api) => {
  const axiosParams = options?.axiosParams || {}

  try {
    const result = await axios({
      method: "put",
      url: `${baseUrl}/${path}`,
      data,
      ...axiosParams
    })
    return result.data
  }
  catch (err) {
    await apiErrorHandler(err, options?.statusCodeHandlers)
  }
}

/**
 * @param {string} path 
 * @param {Object} data 
 * @param {ApiOptions} options 
 * @param {string} baseUrl 
 * @returns 
 */
export const post = async (path, data, options = null, baseUrl = BaseUrl.Api) => {
  const axiosParams = options?.axiosParams || {}
  

  try {
    const result = await axios({
      method: "post",
      url: `${baseUrl}/${path}`,
      data,
      ...axiosParams
    })
    return result.data
  }
  catch (err) {
    console.log("post - err", `${baseUrl}/${path}`, err);
    await apiErrorHandler(err, options?.statusCodeHandlers)
  }
}

/** 
 * @param {string} path 
 * @param {ApiOptions} options 
 * @param {string} baseUrl 
 * @returns 
 */
export const destroy = async (path, options = null, baseUrl = BaseUrl.Api) => {
  const axiosParams = options?.axiosParams || {}
  try {
    const result = await axios({
      method: "delete",
      url: `${baseUrl}/${path}`,
      ...axiosParams
    })
    return result.data
  }
  catch (err) {
    await apiErrorHandler(err, options?.statusCodeHandlers)
  }
}

// const addSafeMessageExceptionHandlerToOptions = (options) => {
//   let safeMessageExceptionHandler = {
//     statusCode: 500
//   }
//   if (options === null || options === undefined)
//     options = {statusCodeHandlers: []};
//   if (options.HasOwnProperty("statusCodeHandlers")) {
//     options.push(safeMessageExceptionHandler);
//   } else {

//   }
// }